// TIN-23 Parte 2: la campana del navbar abre un Offcanvas moderno
// (PanelNotificaciones). Mantenemos la suscripción Socket.io y la
// preferencia de silencio persistida en localStorage.

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getDataList } from '../../api/common';
import { initWebsockets, closeSocket } from '../../libs/http/initWebsocket';
import { Alertassw } from '../Alertas/Alertassw';
import { Howl } from 'howler';
import sonido from '../../sound/SoundNot.mp3';
import moment from 'moment';
import PanelNotificaciones from './PanelNotificaciones';

moment.locale('es');

const LS_SILENCIADO = 'biolab.notificaciones.silenciado';

export const ConsultNot = () => {
  const { userData } = useAuth();
  const [notificaciones, setNotificaciones] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [silenciado, setSilenciado] = useState(() => {
    try {
      return localStorage.getItem(LS_SILENCIADO) === 'true';
    } catch {
      return false;
    }
  });
  const socketRef = useRef(null);

  const toggleSilenciar = () => {
    setSilenciado((prev) => {
      const nuevo = !prev;
      try {
        localStorage.setItem(LS_SILENCIADO, String(nuevo));
      } catch {}
      return nuevo;
    });
  };

  useEffect(() => {
    async function consultarNot() {
      try {
        const resul = await getDataList('Notificacion', userData);
        setNotificaciones(resul?.data?.data || []);
      } catch (error) {
        console.error('[Notif] error consultando:', error);
      }
    }
    if (userData?._id) consultarNot();
  }, [refresh, userData?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket: nueva notificación → toast + sonido + refresh.
  // IMPORTANTE: inicializamos el socket SOLO cuando ya tenemos usuario
  // autenticado. Antes la instancia se creaba a nivel de módulo, lo que
  // disparaba la conexión con token=null (socket rechazado por el server
  // con UNAUTHORIZED) y nunca se reconectaba al hacer login — por eso en
  // una segunda sesión / otro perfil no llegaban las notificaciones hasta
  // refrescar la página.
  useEffect(() => {
    if (!userData?._id) return;

    // Si cambia el user (logout + login o cambio de perfil), reiniciar.
    try { closeSocket(); } catch {}
    const socket = initWebsockets();
    socketRef.current = socket;

    const onNuevaNotificacion = (data) => {
      const usersArr = Array.isArray(data?.users) ? data.users : [];
      const rolesArr = Array.isArray(data?.roles) ? data.roles : [];
      const leidaPorArr = Array.isArray(data?.leidaPor) ? data.leidaPor : [];

      // `userData.role` puede ser objeto populado o string crudo según venga
      // del login/useAuth. Soportamos ambos para no fallar silenciosamente.
      const miUserId = String(userData?._id || '');
      const miRolId = String(userData?.role?._id || userData?.role || '');

      const usuarioPresente = miUserId && usersArr.some(
        (u) => String(u?._id || u) === miUserId
      );
      const rolPresente = miRolId && rolesArr.some(
        (r) => String(r?._id || r) === miRolId
      );

      // Si la notif viene pre-marcada como "leída por mí" (porque yo fui el
      // ejecutor del cambio), NO mostramos toast ni sonamos. El email ya me
      // llegó como responsable; evitamos ruido en mi propia campana.
      const yaLeidaPorMi = miUserId && leidaPorArr.some(
        (l) => String(l?.usuarioId?._id || l?.usuarioId || l) === miUserId
      );

      if ((usuarioPresente || rolPresente) && !yaLeidaPorMi) {
        Alertassw('toast', 'Nueva notificación recibida');
        if (!silenciado) {
          try {
            new Howl({ src: [sonido] }).play();
          } catch {}
        }
        setRefresh((r) => !r);
      }
    };

    socket.on('nuevaNotificacion', onNuevaNotificacion);

    return () => {
      socket.off('nuevaNotificacion', onNuevaNotificacion);
    };
  }, [userData?._id, silenciado]); // eslint-disable-line react-hooks/exhaustive-deps

  const cantidad = notificaciones?.length || 0;
  const cantidadMostrada = cantidad > 99 ? '99+' : cantidad;

  return (
    <>
      <li className="nav-item d-flex align-items-center">
        <a
          className="icono-link i-false position-relative"
          role="button"
          tabIndex={0}
          onClick={() => setShowPanel(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setShowPanel(true);
          }}
          title="Notificaciones"
          style={{ cursor: 'pointer' }}
        >
          <i className="bi bi-bell"></i>
          {cantidad > 0 && (
            <span className="badge badge-danger rounded-circle">{cantidadMostrada}</span>
          )}
        </a>
      </li>

      <PanelNotificaciones
        show={showPanel}
        onHide={() => setShowPanel(false)}
        notificaciones={notificaciones}
        userData={userData}
        onRefresh={() => setRefresh((r) => !r)}
        silenciado={silenciado}
        onToggleSilenciar={toggleSilenciar}
      />
    </>
  );
};
